import { AlertCircle, Eye, EyeOff, Info, Loader2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import type { FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ApiError, NetworkError } from '@/lib/api-client';
import { computePostAuthRedirect } from '@/lib/post-auth-redirect';
import { useAuthStore } from '@/stores/authStore';
import { STRINGS } from '@/strings/strings';

type LoginErrorVariant =
  | 'invalidCredentials'
  | 'throttled'
  | 'generic'
  | 'network'
  | 'prelaunchLocked';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function LoginRoute() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const login = useAuthStore((s) => s.login);
  const status = useAuthStore((s) => s.status);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [formError, setFormError] = useState<LoginErrorVariant | null>(null);

  // Batch 9C A-15 polish (Flag 5) — neutral info banner shown after
  // Account-Delete redirects here. Captured into local state on first
  // render so a subsequent replaceState clear doesn't drop the banner.
  // The query param is then stripped via replaceState so a page reload
  // doesn't repaint the banner.
  const [accountDeletedBanner] = useState(() => searchParams.get('accountDeleted') === '1');
  useEffect(() => {
    if (accountDeletedBanner) {
      const url = new URL(window.location.href);
      url.searchParams.delete('accountDeleted');
      window.history.replaceState({}, '', url.toString());
    }
  }, [accountDeletedBanner]);

  // Double-submit guard (2026-10-30). The `status === 'authenticating'`
  // flag only flips INSIDE `authStore.login()`, so a rapid double
  // tap or submit + Enter within one tick can land two POSTs before
  // the store state propagates. The ref gates the handler
  // synchronously; the mirrored state drives the button disable.
  const authSubmittingRef = useRef(false);
  const [authSubmitting, setAuthSubmitting] = useState(false);
  const isSubmitting = status === 'authenticating' || authSubmitting;

  const validateEmail = (value: string): string | null => {
    if (!value.trim()) return STRINGS.auth.errors.emailRequired;
    if (!EMAIL_RE.test(value)) return STRINGS.auth.errors.emailInvalid;
    return null;
  };

  const validatePassword = (value: string): string | null => {
    if (!value.trim()) return STRINGS.auth.errors.passwordRequired;
    return null;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    // Double-submit guard (2026-10-30) — synchronous ref read so a
    // rapid double tap or submit + Enter within one tick cannot
    // reach `login()` twice.
    if (authSubmittingRef.current) return;

    const emailErr = validateEmail(email);
    const passwordErr = validatePassword(password);
    setEmailError(emailErr);
    setPasswordError(passwordErr);
    if (emailErr || passwordErr) return;

    setFormError(null);
    authSubmittingRef.current = true;
    setAuthSubmitting(true);
    try {
      await login({ email, password });
      const user = useAuthStore.getState().user;
      if (!user) {
        navigate('/auth/login', { replace: true });
        return;
      }
      const returnTo = searchParams.get('returnTo');
      navigate(computePostAuthRedirect(user, returnTo), { replace: true });
    } catch (err) {
      if (err instanceof NetworkError) {
        setFormError('network');
      } else if (err instanceof ApiError) {
        // Backend #42 (2026-05-01): prelaunch gate fires BEFORE the
        // existing httpStatus discriminator because 403 is shared with
        // email_not_verified — error_code is the only reliable signal.
        if (err.errorCode === 'prelaunch_locked') {
          setFormError('prelaunchLocked');
        } else if (err.httpStatus === 401) {
          setFormError('invalidCredentials');
        } else if (err.httpStatus === 429) {
          // Backend throttle: 5 attempts / 5 min per IP
          // (auth.controller.ts). The TTL in the user-facing copy
          // matches the backend `@Throttle` window.
          setFormError('throttled');
        } else {
          setFormError('generic');
        }
      } else {
        setFormError('generic');
      }
    } finally {
      authSubmittingRef.current = false;
      setAuthSubmitting(false);
    }
  };

  const errorMessage =
    formError === 'invalidCredentials'
      ? STRINGS.auth.errors.invalidCredentials
      : formError === 'throttled'
        ? STRINGS.auth.errors.loginThrottled
        : formError === 'network'
          ? STRINGS.auth.errors.networkError
          : formError === 'prelaunchLocked'
            ? STRINGS.auth.errors.prelaunchLocked
            : formError === 'generic'
              ? STRINGS.auth.errors.genericBackend
              : null;

  return (
    <>
      <h1 className="text-[22px] font-semibold text-dream-white mb-6">
        {STRINGS.auth.login.title}
      </h1>

      {accountDeletedBanner && (
        <div
          role="status"
          data-testid="login-account-deleted-banner"
          className="flex items-start gap-2 mb-4 p-3 rounded-md bg-white/10 border border-white/20 text-dream-white/90 text-sm"
        >
          <Info className="w-4 h-4 shrink-0 mt-0.5" />
          <span>{STRINGS.auth.login.accountDeletedBanner}</span>
        </div>
      )}

      {errorMessage && (
        <div
          role="alert"
          className="flex items-start gap-2 mb-4 p-3 rounded-md bg-[rgba(226,75,74,0.15)] border border-[rgba(226,75,74,0.4)] text-[#FCA5A5] text-sm"
        >
          <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
          <span>{errorMessage}</span>
        </div>
      )}

      <form onSubmit={handleSubmit} noValidate className="space-y-4">
        <div className="space-y-1.5">
          <Label htmlFor="login-email" className="text-sm text-dream-white">
            {STRINGS.auth.login.emailLabel}
          </Label>
          <Input
            id="login-email"
            type="email"
            autoComplete="email"
            inputMode="email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              if (emailError) setEmailError(null);
            }}
            onBlur={() => setEmailError(validateEmail(email))}
            aria-invalid={Boolean(emailError)}
            aria-describedby={emailError ? 'login-email-error' : undefined}
            className="h-11 bg-dream-white text-dream-navy"
          />
          {emailError && (
            <p id="login-email-error" className="text-xs text-[color:var(--color-error)]">
              {emailError}
            </p>
          )}
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="login-password" className="text-sm text-dream-white">
            {STRINGS.auth.login.passwordLabel}
          </Label>
          <div className="relative">
            <Input
              id="login-password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                if (passwordError) setPasswordError(null);
              }}
              onBlur={() => setPasswordError(validatePassword(password))}
              aria-invalid={Boolean(passwordError)}
              aria-describedby={passwordError ? 'login-password-error' : undefined}
              className="h-11 pr-10 bg-dream-white text-dream-navy"
            />
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              aria-label={
                showPassword ? STRINGS.auth.login.hidePassword : STRINGS.auth.login.showPassword
              }
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-muted-foreground hover:text-dream-navy transition-colors bg-dream-white"
            >
              {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
          {passwordError && (
            <p id="login-password-error" className="text-xs text-[color:var(--color-error)]">
              {passwordError}
            </p>
          )}
        </div>

        <div className="flex justify-end">
          <Link
            to="/auth/forgot"
            className="text-xs text-dream-white/80 underline hover:text-dream-white"
          >
            {STRINGS.auth.login.forgotPassword}
          </Link>
        </div>

        <Button
          type="submit"
          disabled={isSubmitting}
          className="w-full h-12 text-base font-semibold"
        >
          {isSubmitting && <Loader2 className="w-4 h-4 animate-spin" />}
          {isSubmitting ? STRINGS.auth.login.loadingState : STRINGS.auth.login.submitButton}
        </Button>
      </form>

      <div className="mt-6 pt-6 border-t border-white/10 text-center text-sm">
        <span className="text-dream-white/70">{STRINGS.auth.login.noAccount}</span>{' '}
        <Link
          to="/auth/signup"
          className="text-dream-white font-medium underline hover:text-dream-gold"
        >
          {STRINGS.auth.login.signupLink}
        </Link>
      </div>
    </>
  );
}
